import React, { CSSProperties, useContext } from 'react';
import NumericKeyboard, { IsolateKeyboard } from '@/components/NumericKeyboard';
import { PaymentChannel } from '@/models/payment';
import styles from '../style.less';
import { CloseCircleOutlined, PushpinOutlined, WalletOutlined } from '@ant-design/icons';
import Price from '@/components/Price';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { getPlusAmount, RechargeDiscountType } from '@/models/poiConfig';
import BigNumber from 'bignumber.js';
import PosButton from '@/components/PosButton';
import { PreOrder } from '@/models/order';
import { useSettlementStore } from '../store/useSettlementStore';
import classNames from 'classnames';

type Props = {
  userFullInfo: PreOrder['userFullInfo'];
};

const inlineStyles: { [k: string]: CSSProperties } = {
  balanceCodeWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  balanceCodeLabel: {
    paddingRight: 10,
  },
};

/**
 * 充值模式交易区域
 * 
 * 管理充值主金额、赠送金额、支付密码输入以及与门店充值优惠规则（满送/每满送）的联动。
 */
const RechargeTradeArea: React.FC<Props> = ({ userFullInfo }: Props) => {
  const [
    {
      poiConfig: { rechargeConfig },
    },
  ] = useContext(BaseInfoContext);

  // 从 Zustand Store 订阅充值金额、赠送金额与密码输入状态
  const balanceCode = useSettlementStore(s => s.balanceCode);
  const setBalanceCode = useSettlementStore(s => s.setBalanceCode);
  const actualPrice = useSettlementStore(s => s.actualPrice);
  const setActualPrice = useSettlementStore(s => s.setActualPrice);
  const rechargePlusPrice = useSettlementStore(s => s.rechargePlusPrice);
  const setRechargePlusPrice = useSettlementStore(s => s.setRechargePlusPrice);
  const setPaymentChannel = useSettlementStore(s => s.setPaymentChannel);

  const hasOpenBalance = userFullInfo && userFullInfo.user && userFullInfo.user.balance !== undefined;

  // 暂不启用该功能
  const requireBalanceCode = !hasOpenBalance && userFullInfo && false;

  const actualPriceBigNum = actualPrice ? new BigNumber(actualPrice) : new BigNumber(0);
  const rechargePlusPriceNum = rechargePlusPrice ? new BigNumber(rechargePlusPrice) : new BigNumber(0);

  return (
    <>
      <div
        className={classNames(styles.item, {
          [styles.error]: !userFullInfo,
          [styles.warning]: requireBalanceCode,
          [styles.success]: hasOpenBalance,
        })}>
        <h3 className={`${requireBalanceCode ? '' : styles.single}`}>
          {userFullInfo ? (
            <>
              <WalletOutlined />{' '}
              {hasOpenBalance ? (
                <>
                  当前账户余额: <Price value={userFullInfo.user.balance} />
                </>
              ) : (
                <>当前账户首次充值，请推荐客户设置支付密码</>
              )}
            </>
          ) : (
            <>
              <CloseCircleOutlined /> 充值前请先关联客户
            </>
          )}
        </h3>
        {requireBalanceCode && (
          <div>
            <div style={inlineStyles.balanceCodeWrapper}>
              <IsolateKeyboard
                value={balanceCode}
                onChange={val => setBalanceCode(val)}
                showMask
                keyboardProps={{
                  maxLength: 6,
                  showAllClean: true,
                }}
              />
            </div>
            <div style={{ textAlign: 'center', fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
              6位支付密码，该密码将用于余额支付时验证
            </div>
          </div>
        )}
      </div>
      <div className={`${styles.item} ${styles.fullSize} overflow-hidden`}>
        <NumericKeyboard
          value={actualPrice ? actualPrice.toNumber() : undefined}
          className={styles.rechargeKeyboard}
          placeholder={'请输入充值金额'}
          parseValueToNumber
          precision={2}
          onChange={val => {
            setActualPrice(val);
            if (!val) {
              setPaymentChannel(PaymentChannel.Online);
            } else {
              setRechargePlusPrice(getPlusAmount(rechargeConfig, Number(val)));
            }
          }}
        />
      </div>
      {rechargeConfig.discountType !== RechargeDiscountType.OFF && (
        <div className={styles.item}>
          <h3 style={{ display: 'flex', alignItems: 'center' }}>
            <PushpinOutlined /> 充值优惠
            {actualPriceBigNum.isPositive() && !actualPriceBigNum.isZero() && (
              <div style={{ fontSize: 14, flex: 1, textAlign: 'right' }}>
                充 <Price value={actualPriceBigNum} /> 送 <Price value={rechargePlusPriceNum} /> 实得{' '}
                <Price value={actualPriceBigNum.plus(rechargePlusPriceNum)} type={'green'} />
              </div>
            )}
          </h3>

          {rechargeConfig.discountType === RechargeDiscountType.Amount_PLUS_Amount && (
            <div className={styles.rechargeRules}>
              {rechargeConfig.amountPlusAmount.map(item => {
                return (
                  <div key={`${item.matchAmount}-${item.plusAmount}`}>
                    <PosButton
                      type={'dashed'}
                      size={'middle'}
                      onClick={() => {
                        setActualPrice(item.matchAmount);
                        setRechargePlusPrice(item.plusAmount);
                      }}>
                      <>
                        满 <Price value={item.matchAmount} /> 送 <Price value={item.plusAmount} />
                      </>
                    </PosButton>
                  </div>
                );
              })}
            </div>
          )}

          {rechargeConfig.discountType === RechargeDiscountType.Amount_PLUS_Amount_Multi && (
            <>
              <div>
                每满 <Price value={rechargeConfig.amountPlusAmountMulti.perMatchAmount} /> 送{' '}
                <Price value={rechargeConfig.amountPlusAmountMulti.plusAmount} /> 元
                {rechargeConfig.amountPlusAmountMulti.plusLimit > 0 && (
                  <>
                    ，最多送 <Price value={rechargeConfig.amountPlusAmountMulti.plusLimit} /> 元
                  </>
                )}
              </div>
              {rechargeConfig.amountPlusAmountMultiPresets && rechargeConfig.amountPlusAmountMultiPresets.length ? (
                <div className={styles.rechargeRules} style={{ paddingTop: 10 }}>
                  {rechargeConfig.amountPlusAmountMultiPresets.map(amount => {
                    const plusAmount = getPlusAmount(rechargeConfig, amount);
                    return (
                      <div key={`${amount}`}>
                        <PosButton
                          type={'dashed'}
                          size={'middle'}
                          onClick={() => {
                            setActualPrice(amount);
                            setRechargePlusPrice(plusAmount);
                          }}>
                          <>
                            充 <Price value={amount} /> 送 <Price value={plusAmount} />
                          </>
                        </PosButton>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default RechargeTradeArea;
